import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';
import { ChakraProvider } from '@chakra-ui/react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { SetContextLink } from '@apollo/client/link/context';
import { system } from './theme';
import { UserProvider } from './context/UserContext';
import { isPublicPath } from './utils';

type GraphQLErrorLike = {
  message?: string;
  extensions?: { code?: string };
};

type GraphQLErrorContainer = {
  errors?: GraphQLErrorLike[];
};

type NetworkErrorLike = {
  statusCode?: number;
};

type ErrorLinkContext = Parameters<
  ConstructorParameters<typeof ErrorLink>[0]
>[0];

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getGraphQLErrors = (value: unknown): GraphQLErrorLike[] => {
  if (!isObject(value) || !Array.isArray(value.errors)) {
    return [];
  }

  return value.errors.filter(isObject) as GraphQLErrorLike[];
};

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = ReactDOM.createRoot(container);

const httpLink = new HttpLink({
  uri:
    import.meta.env.VITE_GRAPHQL_URI ||
    (import.meta.env.MODE === 'production' ? '/api' : 'http://localhost:4000/'),
});

const authLink = new SetContextLink(({ headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('loginToken');
  // return the headers to the context so httpLink can read them
  const authHeaders = token ? { authorization: token } : {};
  return {
    headers: {
      ...headers,
      ...authHeaders,
    },
  };
});

const errorLink = new ErrorLink((errorObj: ErrorLinkContext) => {
  const context = errorObj as unknown as Record<string, unknown>;
  const networkError = context.networkError as NetworkErrorLike | undefined;
  const result = context.result as GraphQLErrorContainer | undefined;
  const response = context.response as GraphQLErrorContainer | undefined;
  const error = context.error as GraphQLErrorContainer | undefined;
  const graphQLErrors = getGraphQLErrors(context.graphQLErrors);
  let shouldLogout = false;

  // Collect all errors to check
  const errorsToCheck: GraphQLErrorLike[] = [];
  if (graphQLErrors) errorsToCheck.push(...graphQLErrors);
  if (result?.errors) errorsToCheck.push(...result.errors);
  if (response?.errors) errorsToCheck.push(...response.errors);
  if (error?.errors) errorsToCheck.push(...error.errors);

  errorsToCheck.forEach(err => {
    if (
      err.extensions?.code === 'UNAUTHENTICATED' ||
      err.message?.includes('You need to be logged in') ||
      err.message?.includes('Invalid login')
    ) {
      shouldLogout = true;
    }
  });

  if (networkError) {
    if (networkError.statusCode === 401) {
      shouldLogout = true;
    }
  }

  if (shouldLogout) {
    localStorage.removeItem('loginToken');

    // Public pages (e.g. the ohsnap365 archive) work without a login, so an
    // auth error there just drops the stale token instead of redirecting.
    if (
      window.location.pathname !== '/login' &&
      !isPublicPath(window.location.pathname)
    ) {
      localStorage.setItem('pre_login_path', window.location.pathname);
      window.location.href = '/login';
    }
  }
});

const wsUrl =
  (import.meta.env.VITE_GRAPHQL_URI || 'http://localhost:4000/')
    .replace('http', 'ws')
    .replace(/\/$/, '') + '/graphql';

const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUrl,
    on: {
      connected: () => {
        window.dispatchEvent(new Event('graphql-ws:connected'));
      },
    },
    connectionParams: () => {
      const token = localStorage.getItem('loginToken');
      return {
        authorization: token ? token : undefined,
      };
    },
    lazy: false,
    retryAttempts: Infinity, // Keep retrying effectively forever
  })
);

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  errorLink.concat(authLink).concat(httpLink)
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

root.render(
  <ApolloProvider client={client}>
    <UserProvider>
      <ChakraProvider value={system}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ChakraProvider>
    </UserProvider>
  </ApolloProvider>
);
